import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconArrowDown from '@assets/images/icons/arr_down.svg'
import { makeStyles } from '@mui/styles'
import PopperMenu from '@/modules/base/components/popper-menu/popper-menu'
import theme from '@/theme'
import { ButtonIconSquare, SquareIconButton } from '@/modules/base/components/buttons'
import { VARIANTS as SQUARE_ICON_VARIANTS } from '@/modules/base/components/buttons/square-icon-button/square-icon-button'
import { VARIANTS as BUTTON_SQUARE_ICON_VARIANTS } from '@/modules/base/components/buttons/icon-square/button-icon-square'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import Direction from '@/enums/direction.enum'

export const VARIANTS = Object.freeze({
  primary: SQUARE_ICON_VARIANTS.primary,
  outline: SQUARE_ICON_VARIANTS.outline,
  icon: SQUARE_ICON_VARIANTS.icon,
})

const useStyles = makeStyles((theme) => ({
  tooltip: {
    color: theme.black,
  },
  childrenWrapper: {},
  buttonGroup: {
    display: 'flex',
  },
  mainButton: ({ hasChildren }) => hasChildren && ({
    borderTopRightRadius: '0px',
    borderBottomRightRadius: '0px',
    '& > *': {
      borderRadius: 0,
    },
  }),
  downButton: (variant) => ({
    borderTopLeftRadius: '0px',
    borderBottomLeftRadius: '0px',
    border: variant !== VARIANTS.outline ? `1px solid ${theme.primary}` : 'none',
    borderLeft: '1px solid rgba(255, 255, 255, 0.5)',
    '&:disabled': {
      border: 'none',
      borderLeft: '1px solid rgba(255, 255, 255, 0.5)',
      background: theme.midGrey,
      '& svg': {
        fill: theme.white,
      },
    },
  }),
  popper: {
    '&[data-popper-placement*="top-end"] $childrenWrapper': {
      display: 'flex',
      flexDirection: 'column-reverse',
      '& .dropDownItem': {
        '&:last-of-type': {
          borderBottom: theme.border,
        },
        '&:first-of-type': {
          borderBottom: 'none',
        },
      },
    },
  },
  iconButton: {
    width: 20,
    height: 20,
    cursor: 'pointer',
    padding: 0,
    border: 0,
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:focus-visible': {
      outline: `2px solid ${theme.primary}`,
      outlineOffset: 2,
    },
  },
}))

const SplitButton = props => {
  const {
    variant = VARIANTS.primary,
    className,
    size,
    popperStyle,
    autoClosePopper,
    children,
    onArrowButtonClick,
    disabled,
    deepDisable,
    tooltip,
    tooltipWidth = 300,
    tooltipPlacement = Direction.BOTTOM,
    placement = Direction.BOTTOM_END,
    dataQsId,
    arrowButtonAriaLabel = 'Weitere Aktionen',
    ...rest
  } = props

  const hasChildren = React.Children.toArray(props.children).some(Boolean)
  const classes = useStyles({ variant, hasChildren })

  const buttonGroupRef = useRef()
  const menuRef = useRef()
  // Whether the last open was triggered by keyboard (Enter/Space ⇒ event.detail === 0).
  // Mouse clicks must NOT move focus into the menu: with disablePortal the browser would
  // scroll the focused item into view and legacy detail pages can jump.
  const openedViaKeyboardRef = useRef(false)
  const menuId = `split-button-menu-${useId()}`
  const [popperOpen, setPopperOpen] = useState(false)
  const [isDebouncing, setIsDebouncing] = useState(false)

  const variantButtonIconSquare = variant === VARIANTS.primary ? BUTTON_SQUARE_ICON_VARIANTS.fill : BUTTON_SQUARE_ICON_VARIANTS.outline

  const focusArrowButton = useCallback(() => {
    const buttonGroup = buttonGroupRef.current
    const buttons = buttonGroup?.querySelectorAll?.('button')
    const arrowButton = buttonGroup?.matches?.('button')
      ? buttonGroup
      : buttons?.[buttons.length - 1]

    arrowButton?.focus()
  }, [])

  const handleListKeyDown = useCallback((event) => {
    if (event.key === 'Tab') {
      setPopperOpen(false)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setPopperOpen(false)
      focusArrowButton()
    }
  }, [focusArrowButton])

  useEffect(() => {
    if (!popperOpen) return
    // Only keyboard users get the WAI-ARIA menu focus; mouse behaviour stays as before
    // the a11y retrofit (menu opens, focus stays put).
    if (!openedViaKeyboardRef.current) return

    const firstEnabledItem = menuRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])')
    const firstItem = firstEnabledItem || menuRef.current?.querySelector('[role="menuitem"]')
    firstItem?.focus()
  }, [popperOpen])

  const onDebounceStartHandle = useCallback(() => {
    setIsDebouncing(true)
  }, [])
  const onDebounceEndHandle = useCallback(() => {
    setIsDebouncing(false)
  }, [])

  let buttonContent = (<>
    {variant !== VARIANTS.icon
      ? (
        <div className={clsx(classes.buttonGroup, className)} ref={buttonGroupRef} data-qs-id={dataQsId}>
          <ButtonIconSquare
            className={classes.mainButton}
            variant={variantButtonIconSquare}
            disabled={disabled}
            onDebounceStart={onDebounceStartHandle}
            onDebounceEnd={onDebounceEndHandle}
            dataQsId={`${dataQsId}-main-button`}
            {...rest}
          />
          {hasChildren && <SquareIconButton icon={IconArrowDown}
            variant={variant}
            size="large"
            iconColor={variant === VARIANTS.outline ? theme.primary : theme.white}
            className={classes.downButton}
            disabled={(deepDisable && disabled) || isDebouncing}
            ariaLabel={arrowButtonAriaLabel}
            ariaHasPopup="menu"
            ariaExpanded={popperOpen}
            ariaControls={menuId}
            dataQsId={`${dataQsId}-arrow-button`}
            onClick={(e) => {
              openedViaKeyboardRef.current = e?.detail === 0
              setPopperOpen(current => !current)
              onArrowButtonClick && onArrowButtonClick(e)
            }}
          />}
        </div>
      )
      : (
        <button
          type='button'
          data-qs-id={dataQsId}
          className={classes.iconButton}
          ref={buttonGroupRef}
          aria-label={arrowButtonAriaLabel}
          aria-haspopup='menu'
          aria-expanded={popperOpen}
          aria-controls={menuId}
          onClick={(e) => {
            openedViaKeyboardRef.current = e?.detail === 0
            setPopperOpen(current => !current)
            onArrowButtonClick && onArrowButtonClick(e)
          }}
        >
          <SVGIcon svg={IconArrowDown} height='20px' width='20px' />
        </button>
      )
    }
    { children
    && <PopperMenu
      arrow={false}
      open={popperOpen}
      popperClass={classes.popper}
      elementRef={buttonGroupRef}
      style={popperStyle}
      disablePortal={true}
      placement={placement}
      onClick={() => autoClosePopper && setPopperOpen(false)}
      onClose={() => setPopperOpen(false)}>
      <div
        id={menuId}
        ref={menuRef}
        role='menu'
        onKeyDown={handleListKeyDown}
        className={classes.childrenWrapper}
      >
        {React.Children.map(children, (child, i) =>
          React.isValidElement(child)
            ? React.cloneElement(child, { dataQsId: child.props.dataQsId ?? `${dataQsId}-${i}` })
            : child,
        )}
      </div>
    </PopperMenu>
    }
  </>)

  if (tooltip) {
    buttonContent = (<RichTooltip
      content={(<div className={classes.tooltip}>{tooltip}</div>)}
      placement={tooltipPlacement}
      width={tooltipWidth}>
      <div>
        {buttonContent}
      </div>
    </RichTooltip>)
  }

  return (
    <>
      { buttonContent }
    </>
  )
}

SplitButton.propTypes = {
  tooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  disabled: PropTypes.bool,
  deepDisable: PropTypes.bool,
  popperStyle: PropTypes.object,
  tabIndex: PropTypes.number,
  onClick: PropTypes.func,
  onArrowButtonClick: PropTypes.func,
  variant: PropTypes.oneOf([...Object.values(VARIANTS)]),
  tooltipWidth: PropTypes.number,
  tooltipPlacement: PropTypes.oneOf([...Object.values(Direction)]),
  placement: PropTypes.oneOf([...Object.values(Direction)]),
  children: PropTypes.node,
  label: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  debounceTime: PropTypes.number,
  className: PropTypes.string,
  autoClosePopper: PropTypes.bool,
  dataQsId: PropTypes.string.isRequired,
  actionId: PropTypes.string,
  arrowButtonAriaLabel: PropTypes.string,
}

export default SplitButton
